import type { Ref } from 'vue';
import type { Filter, FilterOperator, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, LogicalFilter, LogicalOperator, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FilterConfigResolvableField } from '#layers/filters/components/Filter/utils/resolve-filter-config';
import type { SupportedOperatorsSpec } from '#layers/filters/composables/use-filter-operator';
import { cloneDeep } from 'lodash-es';
import { createFieldFilter, isLogicalFilterAnd } from '#layers/data-model/public';
import { getDefaultOperatorForField } from '#layers/filters/composables/use-filter-operator';

interface LogicalFilterPrimitiveField extends Pick<ResolvedMonospacePrimitiveField, 'apiName'>, FilterConfigResolvableField {}
type LogicalFilterRelationField = Pick<ResolvedMonospaceSingleRelationField, 'apiName'>;

interface UseLogicalFilterOptions {
	addGroupToFront?: boolean;
	findPrimitiveFieldById?: (id: GlobalPrimitiveFieldId) => LogicalFilterPrimitiveField | undefined;
	findSingleRelationFieldById?: (id: GlobalSingleRelationFieldId) => LogicalFilterRelationField | undefined;
	/**
	 * Resolves the narrowing spec for a primitive field's filter operators. Used to narrow
	 * the default operator picked by handleAdd so it matches what FilterSimple shows after
	 * the field is rendered. Omit to skip narrowing (back-compat; operator is the base
	 * primitive-type first option).
	 */
	supportedOperatorsFor?: (field: LogicalFilterPrimitiveField) => SupportedOperatorsSpec | undefined;
	/**
	 * Resolves a field-type's explicit seeded default operator (e.g. datetime seeds `_lt`).
	 * Applied only when within the field's narrowed operators. Omit to keep first-of-list
	 * (back-compat).
	 */
	defaultOperatorFor?: (field: LogicalFilterPrimitiveField) => FilterOperator | undefined;
}

export function useLogicalFilter(filter: Ref<LogicalFilter>, options?: UseLogicalFilterOptions) {
	function handleAdd(fieldPath: GlobalFieldId[]): number | undefined {
		const { findPrimitiveFieldById, findSingleRelationFieldById } = options ?? {};

		// Convert field IDs to API names
		const apiPath: string[] = [];

		for (const id of fieldPath) {
			const primitiveField = findPrimitiveFieldById?.(id as GlobalPrimitiveFieldId);

			if (primitiveField) {
				apiPath.push(primitiveField.apiName);
				continue;
			}

			const relationField = findSingleRelationFieldById?.(id as GlobalSingleRelationFieldId);

			if (relationField) {
				apiPath.push(relationField.apiName);
				continue;
			}

			// Cannot create filter - field not found in schema
			console.error(`[useLogicalFilter]: Cannot create filter - field not found for ID '${id}'`);
			return undefined;
		}

		const lastFieldId = fieldPath.at(-1);
		const lastField = lastFieldId ? findPrimitiveFieldById?.(lastFieldId as GlobalPrimitiveFieldId) : undefined;
		const spec = lastField ? options?.supportedOperatorsFor?.(lastField) : undefined;
		const seededOperator = lastField ? options?.defaultOperatorFor?.(lastField) : undefined;
		const defaultOperator = getDefaultOperatorForField(lastField, spec, seededOperator);

		// Using undefined (not null) avoids collision with intentional "is null" filters.
		const fieldFilter = createFieldFilter(apiPath, defaultOperator);
		const insertedIndex = getFilterList(filter.value).length;
		filter.value = addFilter(filter.value, fieldFilter);
		return insertedIndex;
	}

	function handleRemove(index: number) {
		filter.value = removeFilterAtIndex(filter.value, index);
	}

	function handleUpdate(index: number, update: Filter) {
		filter.value = cloneDeep(updateFilterAtIndex(filter.value, index, update));
	}

	function handleDuplicate(index: number) {
		const newFilter = cloneDeep(getFilterAtIndex(filter.value, index)!);
		filter.value = insertFilter(filter.value, newFilter, index + 1);
	}

	function handleGroupAdd() {
		filter.value = addGroup(filter.value, options?.addGroupToFront);
	}

	function handleTurnIntoGroup(index: number) {
		const fieldFilter = getFilterAtIndex(filter.value, index)!;
		const group = { _and: [fieldFilter] };
		filter.value = replaceFilterAtIndex(filter.value, index, group);
	}

	function handleToggleOperator() {
		filter.value = isLogicalFilterAnd(filter.value) ? { _or: filter.value._and } : { _and: filter.value._or };
	}

	function handleClearFilter() {
		filter.value = { _and: [] };
	}

	return {
		handleAdd,
		handleRemove,
		handleUpdate,
		handleDuplicate,
		handleTurnIntoGroup,
		handleGroupAdd,
		handleToggleOperator,
		handleClearFilter,
	};
}

export function getFilterList(filter: LogicalFilter): Filter[] {
	return isLogicalFilterAnd(filter)
		? filter._and
		: filter._or;
}

function getFilterAtIndex(filter: LogicalFilter, index: number) {
	return getFilterList(filter)[index];
}

function filterOp(filter: LogicalFilter): LogicalOperator {
	return isLogicalFilterAnd(filter) ? '_and' : '_or';
}

function addGroup<TFilter extends LogicalFilter>(filter: TFilter, toFront?: boolean, group?: Filter): TFilter {
	group ??= { _and: [] };

	const filters = [...getFilterList(filter)];

	if (toFront === true) {
		filters.unshift(group);
	}
	else {
		filters.push(group);
	}

	return { [filterOp(filter)]: filters } as TFilter;
}

function removeFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number): TFilter {
	return {
		[filterOp(filter)]: getFilterList(filter).filter((_, i) => i !== index),
	} as TFilter;
}

function updateFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number, newFilter: Filter): TFilter {
	return {
		[filterOp(filter)]: getFilterList(filter).map((f, i) => (i === index ? newFilter : f)),
	} as TFilter;
}

function addFilter<TFilter extends LogicalFilter>(filter: TFilter, newFilter: Filter): TFilter {
	return {
		[filterOp(filter)]: [...getFilterList(filter), newFilter],
	} as TFilter;
}

function insertFilter<TFilter extends LogicalFilter>(filter: TFilter, newFilter: Filter, index: number): TFilter {
	const filters = [...getFilterList(filter)];
	return {
		[filterOp(filter)]: [
			...filters.slice(0, index),
			newFilter,
			...filters.slice(index),
		],
	} as TFilter;
}

function replaceFilterAtIndex<TFilter extends LogicalFilter>(filter: TFilter, index: number, newFilter: Filter): TFilter {
	const filters = [...getFilterList(filter)];
	return {
		[filterOp(filter)]: [
			...filters.slice(0, index),
			newFilter,
			...filters.slice(index + 1),
		],
	} as TFilter;
}
